/**
 * 大屏登录（方案 A）：未登录跳转升本中心，登录后通过 URL ?key= 回跳慧航大屏。
 *
 * 升本中心登录页需支持 query：
 *   returnUrl  - 慧航完整回跳地址（含 hash，如 https://xx/index.html#/LearnBigData）
 *   isLargeScreen=1
 * 登录成功后跳转：
 *   {origin}{pathname}?key={ticket}#{hashPath}
 * 示例：returnUrl 为 https://hh.com/app.html#/LearnBigData
 *       → https://hh.com/app.html?key=xxx#/LearnBigData
 */
import { GetAdminLoginToken, loginUserInfo } from 'network/login';
import { loginRoleInformationV2 } from 'network/projectLogin';
import { hexToRgb } from './utils';
import { defaultColor, defaultThemeStyle } from './const';

export const LARGE_SCREEN_ROUTE_NAMES = [
  'LearnBigData',
  'AshoreRanking',
  'ExplosiveClassroom',
  'PersonalHonorReport',
];

const DEFAULT_CENTER_URL = 'https://sb.hbzsb.cn';
const STORAGE_DING_ORG_GUEST = 'largeScreenDingOrgGuest';
const STORAGE_DING_ORG_GUEST_TIME = 'largeScreenDingOrgGuestTime';

function getCurrentUrlParam(name) {
  const url = new URL(window.location.href);
  const fromSearch = url.searchParams.get(name);
  if (fromSearch !== null) {
    return fromSearch;
  }
  const hash = url.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }
  return new URLSearchParams(hash.slice(queryIndex + 1)).get(name);
}

export function isLargeScreenRoute(route) {
  return route && LARGE_SCREEN_ROUTE_NAMES.includes(route.name);
}

export function isLoggedIn() {
  const flag = sessionStorage.getItem('isLogin');
  if (flag !== 'true') {
    return false;
  }

  try {
    const token = JSON.parse(sessionStorage.getItem('token') || '{}');
    const currentTime = Date.now() / 1000;
    return Boolean(
      token.AccessToken &&
      token.RefreshToken &&
      token.AccessExpiresIn &&
      token.AccessExpiresIn > currentTime + 60
    );
  } catch (e) {
    return false;
  }
}

export function isDingOrgNonSystemUserCallback() {
  return getCurrentUrlParam('isSystemUser') === '0';
}

export function isLargeScreenDingOrgGuest() {
  return sessionStorage.getItem(STORAGE_DING_ORG_GUEST) === 'true';
}

export function markLargeScreenDingOrgGuest(route) {
  if (!isLargeScreenRoute(route)) {
    return false;
  }
  sessionStorage.setItem(STORAGE_DING_ORG_GUEST, 'true');
  sessionStorage.setItem(STORAGE_DING_ORG_GUEST_TIME, String(Date.now()));
  sessionStorage.setItem('isLargeScreen', 'true');
  return true;
}

export function consumeDingOrgNonSystemUserCallback(route) {
  if (!isDingOrgNonSystemUserCallback()) {
    return false;
  }
  return markLargeScreenDingOrgGuest(route);
}

export function getCenterAdminUrl() {
  try {
    const cache = sessionStorage.getItem('AdminCache');
    if (cache) {
      const raw = JSON.parse(cache).centerAdminUrl;
      if (raw && String(raw).trim()) {
        return String(raw).trim();
      }
    }
  } catch (e) {
    /* ignore */
  }
  try {
    const allConfig = sessionStorage.getItem('allConfig');
    if (allConfig) {
      const cfg = JSON.parse(allConfig);
      const fromCfg = cfg.centerAdminUrl || cfg.centerAdminURL;
      if (fromCfg && String(fromCfg).trim()) {
        return String(fromCfg).trim();
      }
    }
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_CENTER_URL;
}

/** 供升本中心 returnUrl 使用：当前慧航大屏完整地址（含 hash） */
export function buildLargeScreenReturnUrl(route) {
  const hashPath = route?.fullPath || window.location.hash.replace(/^#/, '') || '/';
  const path = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
  return `${window.location.origin}${window.location.pathname}#${path}`;
}

export function buildCenterLoginUrl(returnUrl) {
  const base = getCenterAdminUrl();
  let url;
  try {
    url = new URL(base);
  } catch (e) {
    url = new URL(DEFAULT_CENTER_URL);
  }
  url.searchParams.set('returnUrl', returnUrl || buildLargeScreenReturnUrl());
  url.searchParams.set('isLargeScreen', '1');
  return url.toString();
}

export function getUrlLoginKey() {
  return new URL(window.location.href).searchParams.get('key');
}

/** 清除地址栏 key，保留 hash 路由 */
export function stripKeyFromLocation() {
  const { origin, pathname, hash } = window.location;
  const clean = `${origin}${pathname}${hash || '#/'}`;
  window.history.replaceState({}, document.title, clean);
}

export function stripDingOrgNonSystemUserFromLocation() {
  const url = new URL(window.location.href);
  url.searchParams.delete('isSystemUser');

  let hash = url.hash || '#/';
  const queryIndex = hash.indexOf('?');
  if (queryIndex !== -1) {
    const hashPath = hash.slice(0, queryIndex);
    const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
    hashParams.delete('isSystemUser');
    const hashSearch = hashParams.toString();
    hash = `${hashPath}${hashSearch ? `?${hashSearch}` : ''}`;
  }

  const search = url.searchParams.toString();
  const clean = `${url.origin}${url.pathname}${search ? `?${search}` : ''}${hash}`;
  window.history.replaceState({}, document.title, clean);
}

/**
 * 使用升本中心下发的 key 换取慧航 token 并写入 session
 * @returns {Promise<boolean>}
 */
export async function exchangeKeyForLogin(key, store, onThemeStyle, onLoginError) {
  if (!key) {
    return false;
  }
  try {
    const res = await GetAdminLoginToken({ key });
    if (res?.data?.code != 1 || !res?.data?.result?.access_token) {
      return false;
    }
    const tokenData = res.data.result;
    const expiresTime = tokenData.expires_in + new Date() / 1000;
    const token = {
      AccessToken: tokenData.access_token,
      AccessExpiresIn: expiresTime,
      RefreshToken: tokenData.refresh_token,
    };
    store.commit('saveTokenStorage', token);
    sessionStorage.setItem('token', JSON.stringify(token));

    const result1 = await loginRoleInformationV2();
    if (result1.code != 1) {
      if (typeof onLoginError === 'function') {
        onLoginError(result1.message || '登录失败');
      }
      return false;
    }
    const adminCache = result1.result;
    sessionStorage.setItem('AdminCache', JSON.stringify(adminCache));
    loginUserInfo(adminCache);
    let style = defaultThemeStyle;
    if (adminCache.webStyle) {
      try {
        const webStyle = JSON.parse(adminCache.webStyle);
        webStyle.ThemeColorBGR = hexToRgb(webStyle.ThemeColor);
        style = Object.assign(
          JSON.parse(JSON.stringify(webStyle)),
          defaultColor
        );
        store.commit('changeThemeColor', JSON.stringify(style));
        sessionStorage.setItem('themeStyle', JSON.stringify(style));
      } catch (e) {
        store.commit('changeThemeColor', JSON.stringify(defaultThemeStyle));
        sessionStorage.setItem('themeStyle', JSON.stringify(defaultThemeStyle));
      }
    } else {
      store.commit('changeThemeColor', JSON.stringify(defaultThemeStyle));
      sessionStorage.setItem('themeStyle', JSON.stringify(defaultThemeStyle));
    }

    if (typeof onThemeStyle === 'function') {
      onThemeStyle(style);
    }

    store.commit('changeLoginState', true);
    sessionStorage.setItem('isLogin', 'true');
    sessionStorage.removeItem('isLargeScreen');
    sessionStorage.removeItem('largeScreenPath');
    return true;
  } catch (err) {
    console.error('大屏 key 换票失败:', err);
    return false;
  }
}

/**
 * 未登录时提示并跳转升本中心；已登录或正在处理 key 时不拦截
 * @returns {boolean} true 表示已拦截，调用方应中止后续初始化
 */
export function requireCenterLoginForLargeScreen(vm, route) {
  if (consumeDingOrgNonSystemUserCallback(route)) {
    stripDingOrgNonSystemUserFromLocation();
    return false;
  }
  if (isLargeScreenDingOrgGuest() && isLargeScreenRoute(route)) {
    return false;
  }
  if (getUrlLoginKey()) {
    return false;
  }
  if (isLoggedIn()) {
    return false;
  }

  const returnUrl = buildLargeScreenReturnUrl(route);
  const centerLoginUrl = buildCenterLoginUrl(returnUrl);

  vm.$confirm('请前往升本中心登录后再查看大屏', '提示', {
    confirmButtonText: '去登录',
    cancelButtonText: '取消',
    type: 'info',
  })
    .then(() => {
      window.location.replace(centerLoginUrl);
    })
    .catch(() => {});

  return true;
}
